import React, { useMemo } from 'react';

import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import ReceiptIcon from '@mui/icons-material/Receipt';

import ActivityIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import SupervisedUserIcon from '@mui/icons-material/SupervisedUserCircle';
import WorkIcon from '@mui/icons-material/Work';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PeopleIcon from '@mui/icons-material/People';

import NavigationBar from '@ts-core/components/NavigationBar/NavigationBar';
import { PricingMode } from '@ts-core/utils/Constants';

import { RankingType } from '@ts-core/utils/Constants';
import { useCourseContext } from '@ts-core/context/course';
import { useProfessorAuth } from 'modules/auth/hooks';

import CourseStateComponent from 'modules/students/components/CourseStateComponent';

import { useAcademyAuthUtils } from 'modules/auth/hooks';

const ProfessorNavigationBarContainer = () => {
  const { course } = useCourseContext();

  const { onSignOut, user } = useProfessorAuth();
  const { academyHasFeature, Feature } = useAcademyAuthUtils();

  const academy = user?.academy;

  const topMenuItems = useMemo(() => {
    if (!academy) return [];
    const { pricing } = academy;

    const items: any[] = [
      {
        title: 'drawer.academy',
        to: '/academy',
        icon: SchoolIcon,
        requiredRole: '5a2c3f1e0c3f5c0928526310',
      },
    ];

    if (pricing?.mode === PricingMode.StudentPeriod || pricing?.mode === PricingMode.StudentCourseMonth) {
      items.push({
        title: academyHasFeature(Feature.NewBilling) ? 'drawer.coursePaymentsOverviewLegacy' : 'drawer.coursePaymentsOverview',
        to: '/billing-legacy',
        icon: ReceiptIcon,
        requiredRole: '5a2c3f1e0c3f5c0928526310',
      });
    }

    if (academyHasFeature(Feature.NewBilling)) {
      items.push({
        title: 'drawer.coursePaymentsOverview',
        to: '/billing',
        icon: ReceiptIcon,
        requiredRole: '5a2c3f1e0c3f5c0928526310',
      });
    }

    items.push({
      title: 'drawer.courses',
      to: '/courses',
      icon: ClassIcon,
    });

    return items.filter((i) => !i.requiredRole || i.requiredRole === user?.role?._id);
  }, [academy, user, academyHasFeature, Feature]);

  const courseMenuItem = useMemo(() => {
    if (!course?._id) return [];
    const items = [
      {
        title: 'drawer.course.dashboard',
        to: `/${course._id}/dashboard`,
        icon: DashboardIcon,
      },
      {
        title: 'quickLink.assignments',
        icon: WorkIcon,
        to: `/${course._id}/assignments`,
      },
    ];

    if (academyHasFeature(Feature.Activities)) {
      items.push({
        title: 'quickLink.activities',
        icon: ActivityIcon,
        to: `activities`,
      });
    }

    if (academyHasFeature(Feature.Ranking)) {
      items.push({
        title: 'quickLink.rankings',
        icon: EqualizerIcon,
        to: `/${course._id}/rankings/${RankingType.general}`,
      });
    }

    items.push(
      {
        title: 'quickLink.courseStats',
        icon: FitnessCenterIcon,
        to: `/${course._id}/stats`,
      },
      {
        title: 'quickLink.students',
        icon: SupervisedUserIcon,
        to: `/${course._id}/students`,
      },
      {
        title: 'drawer.course.professors',
        icon: PeopleIcon,
        to: `/${course._id}/professors`,
      }
    );

    return items;
  }, [course, academyHasFeature, Feature]);

  return (
    <NavigationBar
      user={user}
      topMenuItems={topMenuItems}
      currentCourse={course}
      courseMenuItems={courseMenuItem}
      onSignOut={onSignOut}
      profileUrl="/profile"
      CourseStateComponent={CourseStateComponent}
    />
  );
};

export default ProfessorNavigationBarContainer;
